import React, { ReactNode } from 'react'

import CallToAction from '../CallToAction'
import CarouselMapper from '../Carousel/CarouselMapper'
import LinkConverter from '../LinkConverter'
import List, { IconBullet } from '../List'
import ParrotFishIcon, { IParrotfishIcon } from '../ParrotfishIcon'
import Faq from '../Faq'
import Dialog from '../Dialog'
import CallUsGraph from '../CallUsGraph'
import Speedtest from '../Speedtest'
import CoverageMap from '../CoverageMap'
import ContentModule from '../ContentModule/ContentModule'
import BoxModule from '../BoxModule'
import Form from '../Form'
import ColumnsLayout from '../Layout/Columns/Columns'
import CarouselLayout from '../Carousel/Variants/HelpCarousel/Carousel'
import TextComponent from '../Text'
import CardComponent from '../Card'
import HelpPageCategoryCard from '../Card/Variants/HelpPageCategoryCard/HelpPageCategoryCard'
import PriceComparison from '../PriceComparison'
import AccordionComponent from '../Accordion'
import CodeModule from '../CodeModule'
import HelpPageList from '../List/Variants/HelpPageList'
import AlertBanner from '../AlertBanner/AlertBanner'
import Linevisitation from '../Linevisitation'
import TimelineComponent from '../Timeline'
import TabModule from '../TabModule/TabModule'
import ChipComponent from '../Chip'

//types import
import { type CallToActionType } from '../CallToAction'
import { type TCarouselMapper } from '../Carousel'
import { type ILinkConverter } from '../LinkConverter'
import { type IIconBullet, type IList } from '../List'
import { type HelpPageListComponentProps } from '../List/Variants/HelpPageList/HelpPageList.type'
import { type ISpeedtest } from '../Speedtest'
import { type IFaq } from '../Faq/types'
import { type DialogProps } from '../Dialog'
import { type ICallUsGraph } from '../CallUsGraph/types'
import { type IContentModule } from '../ContentModule/types'
import { type IBoxModule } from '../BoxModule'
import { type FormProps } from '../Form'
import { type ColumnsLayoutComponent } from '../Layout/Columns/Columns.type'
import { type CarouselLayoutComponent } from '../Carousel/Variants/HelpCarousel/Carousel'
import { type TextComponentProps } from '../Text'
import { type CardProps } from '../Card'
import { type AccordionComponentProps } from '../Accordion'
import { type HelpPageCategoryComponentProps } from '../Card/Variants/HelpPageCard/HelpPageCard.types'
import { type IPriceComparison } from '../PriceComparison'
import { type IAlertBanner } from '../AlertBanner'
import { type CodeModuleComponentProps } from '../CodeModule'
import { type ILinevisitation } from '../Linevisitation/types'
import { TabModuleProps } from '../TabModule/type'
import DriftInfo from '../Driftsinformation'
import { Modules } from '../Driftsinformation/Enums'
import { type TimelineComponentProps } from '../Timeline'
import { type ChipProps } from '../Chip'
import HelpImage, { HelpImageProps } from '../Image/Variants/HelpImage'
import PricingCalculator from '../PricingCalculator/PricingCalculator'
import Feedback, { IFeedback } from '../Feedback'

export type ContentfulComponentId =
  | 'ContentfulModuleBox'
  | 'ContentfulComponentCta'
  | 'ContentfulModuleCallUsGraph'
  | 'ContentfulLayoutCarousel'
  | 'ContentfulPageCompose'
  | 'ContentfulLayoutColumns'
  | 'HelpUniverseLayoutCarousel'
  | 'ContentfulComponentContentModule'
  | 'ContentfulComponentDialog'
  | 'ContentfulComponentFaq'
  | 'ContentfulModuleForm'
  | 'ContentfulComponentIconBullet'
  | 'ContentfulModuleLinevisitation'
  | 'ContentfulComponentLink'
  | 'ContentfulComponentList'
  | 'ContentfulComponentIcon'
  | 'ContentfulModulePriceComparison'
  | 'ContentfulSpeedtest'
  | 'ContentfulSpeedtestVisual'
  | 'ContentfulCoverageMap'
  | 'ContentfulDriftsInfoMap'
  | 'HelpUniverseLayoutColumns'
  | 'HelpUniverseComponentText'
  | 'HelpUniverseComponentCard'
  | 'HelpUniversePageSubcategory'
  | 'HelpUniverseComponentAccordion'
  | 'HelpUniverseComponentImage'
  | 'HelpUniverseComponentList'
  | 'HelpUniverseComponentAlert'
  | 'HelpUniverseModuleDynamicComponents'
  | 'ContentfulComponentAlert'
  | 'HelpUniverseModuleForm'
  | 'HelpUniverseComponentCta'
  | 'HelpUniverseComponentTabs'
  | 'HelpUniverseComponentIcon'
  | 'DriftInformationTelefoni'
  | 'DriftInformationBredband'
  | 'HelpUniverseComponentTimeline'
  | 'HelpUniverseComponentChip'
  | 'ContentfulPricingCalculator'
  | 'ContentfulModuleDynamicComponents'
  | 'ContentfulComponentFeedback'

export interface IComponentMap {
  internal: {
    type: ContentfulComponentId
  }
  parentModule?: string
  [key: string]: any //allow any other props in this type
}

const ComponentMap = ({ internal, ...props }: IComponentMap): React.ReactElement => {
  const type = internal?.type
  if (!type) {
    console.warn('No type was passed to the ComponentMap')
    return <></>
  }

  const { parentModule, ...rest } = props
  if (type === 'ContentfulComponentFeedback') return <Feedback {...(rest as unknown as IFeedback)} />
  if (type === 'ContentfulComponentAlert') return <AlertBanner {...(rest as unknown as IAlertBanner)} />
  if (type === 'ContentfulModuleBox') return <BoxModule {...(rest as unknown as IBoxModule)} />
  if (type === 'ContentfulComponentCta') return <CallToAction {...(rest as unknown as CallToActionType)} />
  if (type === 'ContentfulLayoutCarousel') return <CarouselMapper {...(rest as unknown as TCarouselMapper)} />
  if (type === 'ContentfulPageCompose') {
    const linkProps = {
      ...(rest as unknown as ILinkConverter),
      linkHref: (rest as { slug: string }).slug,
      children: (rest as { title: ReactNode }).title,
    }

    return <LinkConverter {...linkProps} />
  }
  if (type === 'ContentfulComponentContentModule') return <ContentModule {...(rest as unknown as IContentModule)} />
  if (type === 'ContentfulComponentDialog') return <Dialog {...(rest as unknown as DialogProps)} />
  if (type === 'ContentfulComponentFaq') return <Faq {...(rest as unknown as IFaq)} />
  if (type === 'HelpUniverseComponentTabs') return <TabModule {...(rest as unknown as TabModuleProps)} />
  if (type === 'ContentfulComponentIconBullet') return <IconBullet {...(rest as unknown as IIconBullet)} />
  if (type === 'ContentfulComponentLink') {
    const linkProps = {
      ...(rest as unknown as ILinkConverter),
      linkHref: (rest as { slug: string }).slug,
      children: (rest as { title: ReactNode }).title,
    }

    return <LinkConverter {...linkProps} />
  }
  if (type === 'ContentfulComponentList') return <List {...(rest as unknown as IList)} />
  if (type === 'ContentfulComponentIcon') return <ParrotFishIcon {...(rest as unknown as IParrotfishIcon)} />
  if (type === 'HelpUniverseComponentIcon') return <ParrotFishIcon {...(rest as unknown as IParrotfishIcon)} />
  if (type === 'ContentfulModuleCallUsGraph') return <CallUsGraph {...(rest as unknown as ICallUsGraph)} />
  if (type === 'ContentfulSpeedtest') return <Speedtest {...(rest as unknown as ISpeedtest)} trackingId={type} />
  if (type === 'ContentfulSpeedtestVisual')
    return <Speedtest {...(rest as unknown as ISpeedtest)} trackingId={type} hasVisuals={true} />
  if (type === 'ContentfulCoverageMap') return <CoverageMap variant="coverage" {...rest} />
  if (type === 'ContentfulDriftsInfoMap') return <CoverageMap variant="driftsinfo" {...rest} />
  if (type === 'HelpUniverseLayoutColumns') return <ColumnsLayout {...(rest as unknown as ColumnsLayoutComponent)} />
  if (type === 'HelpUniverseLayoutCarousel') return <CarouselLayout {...(rest as unknown as CarouselLayoutComponent)} />
  if (type === 'HelpUniverseComponentText') return <TextComponent {...(rest as unknown as TextComponentProps)} />
  if (type === 'HelpUniverseComponentCard') return <CardComponent {...(rest as unknown as CardProps)} />
  if (type === 'HelpUniversePageSubcategory')
    return <HelpPageCategoryCard {...(rest as unknown as HelpPageCategoryComponentProps)} />
  if (type === 'HelpUniverseComponentAccordion')
    return <AccordionComponent {...(rest as unknown as AccordionComponentProps)} />
  if (type === 'HelpUniverseComponentList') return <HelpPageList {...(rest as unknown as HelpPageListComponentProps)} />
  if (type === 'HelpUniverseComponentImage') return <HelpImage {...(rest as unknown as HelpImageProps)} />
  if (type === 'HelpUniverseComponentCta') return <CallToAction {...(rest as unknown as CallToActionType)} />
  if (type === 'HelpUniverseModuleDynamicComponents')
    return <CodeModule {...(rest as unknown as CodeModuleComponentProps)} />
  if (type === 'HelpUniverseComponentAlert') return <AlertBanner {...(rest as unknown as IAlertBanner)} />
  if (type === 'ContentfulModuleLinevisitation') return <Linevisitation {...(rest as unknown as ILinevisitation)} />
  if (type === 'ContentfulModulePriceComparison') return <PriceComparison {...(rest as unknown as IPriceComparison)} />
  if (type === 'ContentfulModuleForm') return <Form {...(rest as unknown as FormProps)} />
  if (type === 'HelpUniverseModuleForm') return <Form {...(rest as unknown as FormProps)} />
  if (type === 'DriftInformationTelefoni') return <DriftInfo module={Modules.TELEFONI} {...rest} />
  if (type === 'DriftInformationBredband') return <DriftInfo module={Modules.BREDBAAND} {...rest} />
  if (type === 'HelpUniverseComponentTimeline')
    return <TimelineComponent {...(rest as unknown as TimelineComponentProps)} />
  if (type === 'HelpUniverseComponentChip') return <ChipComponent {...(rest as unknown as ChipProps)} />
  if (type === 'ContentfulModuleDynamicComponents')
    return <CodeModule {...(rest as unknown as CodeModuleComponentProps)} />
  if (type === 'ContentfulPricingCalculator') return <PricingCalculator {...rest} />

  return <p>Unknown component of type: {type}</p>
}

export default ComponentMap
